import { MutableRefObject, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router'

import { getOnboardingStepFlow } from '../constants/onboardingStepFlow'
import { onboardingPathToStep, onboardingStepToPath } from '../constants/onboardingStepPathMapping'
import { OnboardingFinalizedStateType, OnboardingSetupStep } from '../types'
import { isOnboardingFinalized } from '../utils/isOnboardingFinalized'

interface UseOnboardingStepHistorySyncOptions {
  baseUrl?: string
  firstStep?: OnboardingSetupStep
  isExtendedQuestionnaire: boolean
  isFetched: boolean
  onboardingCurrentStep?: OnboardingSetupStep | OnboardingFinalizedStateType
  setStepHistory: (value: OnboardingSetupStep[]) => void
  stepHistoryRef: MutableRefObject<OnboardingSetupStep[]>
  updateCurrentStep: (step: OnboardingSetupStep | OnboardingFinalizedStateType) => void
}

const HISTORY_POP_ACTION = 'POP'

export const useOnboardingStepHistorySync = ({
  baseUrl,
  firstStep,
  isExtendedQuestionnaire,
  isFetched,
  onboardingCurrentStep,
  setStepHistory,
  stepHistoryRef,
  updateCurrentStep,
}: UseOnboardingStepHistorySyncOptions) => {
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const hasSyncedRef = useRef(false)

  useEffect(() => {
    const isReady = isFetched && firstStep && !hasSyncedRef.current
    const isFinalized = onboardingCurrentStep !== undefined && isOnboardingFinalized(onboardingCurrentStep)

    if (!isReady || isFinalized) {
      return
    }

    hasSyncedRef.current = true

    const currentStepFlow = getOnboardingStepFlow(isExtendedQuestionnaire)
    const isResumableStep =
      onboardingCurrentStep !== undefined &&
      Boolean(onboardingStepToPath[onboardingCurrentStep]) &&
      Boolean(currentStepFlow[onboardingCurrentStep])
    const resumeStep = isResumableStep ? onboardingCurrentStep : firstStep

    // Drop steps from a flow the org is no longer assigned to — otherwise stale steps could survive resume and back navigation.
    const currentHistory = stepHistoryRef.current
    const sanitizedHistory = currentHistory.filter((step) => Boolean(currentStepFlow[step]))
    const alreadyResumedIntoHistory =
      resumeStep !== firstStep &&
      sanitizedHistory.length === currentHistory.length &&
      sanitizedHistory.at(-1) === resumeStep

    if (!alreadyResumedIntoHistory) {
      setStepHistory(resumeStep !== firstStep ? [firstStep, resumeStep] : [firstStep])
    }

    navigateRef.current(`${baseUrl}/${onboardingStepToPath[resumeStep]}`, { replace: true })
  }, [baseUrl, firstStep, isExtendedQuestionnaire, isFetched, onboardingCurrentStep, setStepHistory, stepHistoryRef])

  // Sync stepHistory when the browser back button triggers navigation (POP event).
  // Both the in-app back button (via navigate(-1)) and the browser buttons are handled here.
  const location = useLocation()
  const navigationType = useNavigationType()
  /* useLocation()/useNavigationType() also report the current value on mount, unlike history.listen()
     which only ever fired on a subsequent navigation, skip the first invocation to match that. */
  const isInitialRenderRef = useRef(true)

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }

    if (navigationType !== HISTORY_POP_ACTION) {
      return
    }

    if (!baseUrl || !location.pathname.startsWith(baseUrl)) {
      return
    }

    const stepPath = location.pathname.slice(baseUrl.length + 1)
    const step = onboardingPathToStep[stepPath]

    if (!step) {
      return
    }

    const currentHistory = stepHistoryRef.current
    const stepIndex = currentHistory.lastIndexOf(step)

    if (stepIndex !== -1) {
      const newHistory = currentHistory.slice(0, stepIndex + 1)
      setStepHistory(newHistory)
      updateCurrentStep(step)
    }
  }, [baseUrl, location, navigationType, setStepHistory, stepHistoryRef, updateCurrentStep])
}
